import { useEffect } from 'react'
import { getCycledIndex, runWhenInDom } from './useMenuKeyboardNav'

/** Selector for focusable controls inside the BP popover paper. */
const FOCUSABLE =
    'button:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled]), input:not([disabled])'
const FOCUSED_SWITCH_OUTLINE = {
    outline: '2px solid #1899D4',
    outlineOffset: '2px',
    borderRadius: '14px',
}

/**
 * Identifies the BP popover paper structurally: it never contains the main
 * menu item marker ([data-main-menu-item]), unlike the main menu popover.
 * When several candidate papers exist, the BP popover is always the most
 * recently opened, so prefer the last match.
 */
const findBPPaper = (): HTMLElement | null => {
    const papers = Array.from(
        document.querySelectorAll<HTMLElement>('.MuiPopover-paper')
    )
    const candidates = papers.filter(
        (p) => !p.querySelector('[data-main-menu-item]')
    )
    return candidates.length ? candidates[candidates.length - 1] : null
}

/**
 * Focus trap for the Best Practices settings popover.
 *
 * The popover renders MUI Switch rows that are not reachable with Tab/arrow
 * keys by default, so while mounted this hook:
 * - locates the BP paper in the DOM (immediately or via observer as it mounts),
 * - auto-focuses its first focusable control,
 * - cycles focus across all focusable controls on Tab/Shift+Tab and
 *   ArrowUp/ArrowDown, wrapping at both ends,
 * - toggles a visible outline on the focused switch row (MUI v4 switches have
 *   no focus ring of their own here),
 * - clicks the focused checkbox input on Enter.
 *
 * All listeners and observers are removed when the popover unmounts.
 */
export const useBestPracticesKeyTrap = (): void => {
    useEffect(() => {
        let keyHandler: ((e: KeyboardEvent) => void) | null = null
        let focusedSwitchRoot: HTMLElement | null = null
        const disposers: Array<() => void> = []

        const clearFocusedSwitchOutline = () => {
            if (!focusedSwitchRoot) return
            Object.assign(focusedSwitchRoot.style, {
                outline: '',
                outlineOffset: '',
                borderRadius: '',
            })
            focusedSwitchRoot = null
        }

        const markFocusedSwitch = (el: HTMLElement) => {
            clearFocusedSwitchOutline()
            const root = el.closest('.MuiSwitch-root') as HTMLElement | null
            if (root) {
                Object.assign(root.style, FOCUSED_SWITCH_OUTLINE)
                focusedSwitchRoot = root
            }
        }

        const setupKeyTrap = (paper: HTMLElement) => {
            keyHandler = (e: KeyboardEvent) => {
                const items = Array.from(
                    paper.querySelectorAll<HTMLElement>(FOCUSABLE)
                )
                if (!items.length) return

                if (
                    e.key === 'Tab' ||
                    e.key === 'ArrowDown' ||
                    e.key === 'ArrowUp'
                ) {
                    e.preventDefault()
                    e.stopPropagation()
                    const currentIdx = items.indexOf(
                        document.activeElement as HTMLElement
                    )
                    const isBack = e.shiftKey || e.key === 'ArrowUp'
                    const nextIdx = getCycledIndex(
                        currentIdx,
                        items.length,
                        isBack
                    )
                    items[nextIdx].focus()
                    markFocusedSwitch(items[nextIdx])
                } else if (e.key === 'Enter') {
                    const active = document.activeElement as HTMLElement
                    if (
                        active &&
                        items.includes(active) &&
                        active.getAttribute('type') === 'checkbox'
                    ) {
                        e.preventDefault()
                        active.click()
                    }
                }
            }
            document.addEventListener('keydown', keyHandler, true)

            disposers.push(
                runWhenInDom(() => {
                    const first = paper.querySelector<HTMLElement>(FOCUSABLE)
                    if (!first) return false
                    first.focus()
                    markFocusedSwitch(first)
                    return true
                })
            )
        }

        disposers.push(
            runWhenInDom(() => {
                const bpPaper = findBPPaper()
                if (!bpPaper) return false
                setupKeyTrap(bpPaper)
                return true
            })
        )

        return () => {
            disposers.forEach((dispose) => dispose())
            if (keyHandler)
                document.removeEventListener('keydown', keyHandler, true)
            clearFocusedSwitchOutline()
        }
    }, [])
}
